const { fetchAuthorizedJsonApi, fetchJsonApi } = require('./abstract');
const { getApiLanguage } = require('./language');

const versionPrefix = 'v2';

/**
 * @return Promise that return the user when fulfilled.
 */
function fetchUser(apiUrl, token, userId) {
  return fetchAuthorizedJsonApi({
    apiUrl,
    token,
    method: 'GET',
    endpoint: `${versionPrefix}/users/${userId}`,
    expectedStatus: 200,
    description: 'Fetching user',
  });
}

function fetchUserRank(apiUrl, token) {
  return fetchAuthorizedJsonApi({
    apiUrl,
    token,
    method: 'GET',
    endpoint: 'v1/users/self/rank',
    expectedStatus: 200,
    description: 'Fetching user rank',
  });
}

/**
 * @return Promise that updates a user
 */
function updateUser(apiUrl, token, userId = 'self', updatedUser) {
  return fetchAuthorizedJsonApi({
    apiUrl,
    token,
    method: 'PUT',
    endpoint: `${versionPrefix}/users/${userId}`,
    body: updatedUser,
    expectedStatus: 200,
    description: 'Updating user',
  });
}

function deleteUser(apiUrl, token) {
  return fetchAuthorizedJsonApi({
    apiUrl,
    token,
    method: 'DELETE',
    endpoint: `${versionPrefix}/users/self`,
    expectedStatus: 204,
    description: 'Deleting user',
  });
}

// seems to be unused in API
function removeUser(apiUrl, token, userId) {
  return fetchAuthorizedJsonApi({
    apiUrl,
    token,
    method: 'DELETE',
    endpoint: `${versionPrefix}/users/${userId}`,
    expectedStatus: 204,
    description: 'Removing user',
  });
}

function setPassword(
  apiUrl,
  token,
  userId = 'self',
  currentPassword,
  newPassword,
) {
  return fetchAuthorizedJsonApi({
    apiUrl,
    token,
    method: 'PUT',
    endpoint: `${versionPrefix}/users/${userId}/setPassword`,
    body: {
      currentPassword,
      newPassword,
    },
    expectedStatus: 204,
    description: 'Changing password',
  });
}

/**
 * @return Promise that updates a user
 */
function appStart(apiUrl, token) {
  return fetchAuthorizedJsonApi({
    apiUrl,
    token,
    method: 'PUT',
    endpoint: `${versionPrefix}/users/self/appStart`,
    expectedStatus: 200,
    description: 'Getting daily coins',
  });
}

/**
 * @return Promise that verifies a user's email address
 */
function verifyEmail(apiUrl, token, emailVerificationToken, userId = 'self') {
  const endpoint = token
    ? `${versionPrefix}/users/${userId}/verifyEmail`
    : `${versionPrefix}/users/any/verifyEmail`;
  const headers = {};

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return fetchJsonApi({
    apiUrl,
    method: 'PUT',
    endpoint,
    headers,
    body: { emailVerificationToken },
    expectedStatus: 200,
    description: 'Verifying email',
  });
}

function requestEmailVerification(apiUrl, token) {
  return fetchAuthorizedJsonApi({
    apiUrl,
    token,
    method: 'GET',
    endpoint: `${versionPrefix}/users/self/requestEmailVerification`,
    expectedStatus: 204,
    description: 'Requesting email verification code',
  });
}

function setRecruiter(apiUrl, token, recruiterUsername) {
  return fetchAuthorizedJsonApi({
    apiUrl,
    token,
    method: 'POST',
    endpoint: `${versionPrefix}/users/self/recruiter`,
    body: { recruiterUsername },
    expectedStatus: 204,
    description: 'Setting recruiter',
  });
}

function sendIssueEmailToSupport(apiUrl, options, files) {
  const formData = new FormData();
  formData.append('options', JSON.stringify(options));
  Object.keys(files).forEach((file) => formData.append(file, files[file]));
  return fetchJsonApi({
    apiUrl,
    method: 'POST',
    endpoint: 'v1/users/contactSupport',
    headers: { 'Content-Type': null },
    body: formData,
    expectedStatus: 204,
    description: 'Sending ticket to support',
  });
}

function claimMonthlyDiamondBonus(apiUrl, token) {
  return fetchAuthorizedJsonApi({
    apiUrl,
    token,
    method: 'PUT',
    endpoint: 'v1/users/self/monthly-diamonds',
    expectedStatus: 200,
    description: 'Claiming monthly diamond bonus',
  });
}

function claimMonthlyStoreDiscount(apiUrl, token) {
  return fetchAuthorizedJsonApi({
    apiUrl,
    token,
    method: 'PUT',
    endpoint: 'v1/users/self/monthly-discount',
    query: { lang: getApiLanguage() },
    expectedStatus: 200,
    description: 'Claiming monthly store discount',
  });
}

function transferData(apiUrl, token, { encryptedToken }) {
  return fetchAuthorizedJsonApi({
    apiUrl,
    token,
    method: 'PUT',
    endpoint: 'v1/users/self/data-transfer',
    body: { token: encryptedToken },
    expectedStatus: 200,
    description: 'Transferring data from different account',
  });
}

function setFavourites(apiUrl, token, favouriteLoots) {
  return fetchAuthorizedJsonApi({
    apiUrl,
    token,
    method: 'PUT',
    endpoint: 'v1/users/self/favourites',
    body: { favouriteLoots },
    expectedStatus: 204,
    description: 'Setting favourites',
  });
}

function subscribeToNewsletter(apiUrl, token, newsletterEmail, captchaToken) {
  const requestData = {
    apiUrl,
    method: 'PUT',
    endpoint: 'v1/users/newsletter',
    body: {
      newsletterEmail,
      'g-recaptcha-response': captchaToken,
    },
    expectedStatus: 200,
    description: 'Subscribing to newsletter',
  };
  if (token) {
    return fetchAuthorizedJsonApi({
      ...requestData,
      token,
    });
  }
  return fetchJsonApi(requestData);
}

module.exports = {
  fetchUser,
  fetchUserRank,
  updateUser,
  deleteUser,
  removeUser,
  setPassword,
  appStart,
  verifyEmail,
  requestEmailVerification,
  setRecruiter,
  sendIssueEmailToSupport,
  claimMonthlyStoreDiscount,
  claimMonthlyDiamondBonus,
  transferData,
  setFavourites,
  subscribeToNewsletter,
};
